import React, { useState, useRef, useEffect } from "react";
import { Button } from "../Button";
import { BusinessAccountService } from "../../api/generated/services/BusinessAccountService";

interface PartnerActionsPopoverProps {
  companyId: string;
}

const PartnerActionsPopover: React.FC<PartnerActionsPopoverProps> = ({
  companyId,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.bottom + 8,
        left: rect.right - 192, // 192px = 12rem (w-48)
      });
    }
  }, [isOpen]);

  const handleLoginAsPartner = async () => {
    try {
      setIsLoading(true);
      await BusinessAccountService.postApiBusinessAccountLoginOnBehalfOfCompany({
        companyId,
      });
      // Full page refresh to root on success
      window.location.href = "/";
    } catch (error) {
      console.error("Error logging in as partner:", error);
      setIsLoading(false);
      setIsOpen(false);
    }
  };

  return (
    <>
      <div ref={buttonRef}>
        <Button
          variant="icon"
          onClick={() => setIsOpen(!isOpen)}
          className="p-2"
          disabled={isLoading}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle cx="8" cy="3" r="1.5" />
            <circle cx="8" cy="8" r="1.5" />
            <circle cx="8" cy="13" r="1.5" />
          </svg>
        </Button>
      </div>

      {isOpen && (
        <div
          ref={popoverRef}
          className="fixed w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-[9999]"
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
          }}
        >
          <button
            onClick={handleLoginAsPartner}
            disabled={isLoading}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 cursor-pointer text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? "Logging in..." : "Login as Partner"}
          </button>
        </div>
      )}
    </>
  );
};

export default PartnerActionsPopover;
